/**
 * Presentational panel showing explainable, read-only, completely-unassigned expense
 * invoice suggestions for a booking. Selecting a suggestion only feeds the existing
 * manual assignment editor via `onSelectDocument`; it never saves automatically.
 */

import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useGetBookingExpenseInvoiceSuggestionsLookupQuery } from '../Service/expenseInvoicesLookupApi';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import type {
  BookingExpenseInvoiceSuggestionConfidence,
  BookingExpenseInvoiceSuggestionLookup,
} from '../Service/expenseInvoicesLookupMapper';

export interface BookingExpenseInvoiceSuggestionsPanelProps {
  bookingId: number;
  onSelectDocument: (suggestion: BookingExpenseInvoiceSuggestionLookup) => void;
}

const confidenceLabels: Record<BookingExpenseInvoiceSuggestionConfidence, string> = {
  high: 'Wysokie dopasowanie',
  medium: 'Średnie dopasowanie',
  low: 'Niskie dopasowanie',
};

const confidenceColors: Record<
  BookingExpenseInvoiceSuggestionConfidence,
  'success' | 'warning' | 'default'
> = {
  high: 'success',
  medium: 'warning',
  low: 'default',
};

const BookingExpenseInvoiceSuggestionsPanel: React.FC<BookingExpenseInvoiceSuggestionsPanelProps> = ({
  bookingId,
  onSelectDocument,
}) => {
  const hasValidBookingId = Number.isFinite(bookingId) && bookingId > 0;
  const {
    data: suggestions,
    isLoading,
    isError,
    error,
  } = useGetBookingExpenseInvoiceSuggestionsLookupQuery(bookingId, {
    skip: !hasValidBookingId,
  });

  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}
        data-testid="expense-invoice-suggestions-loading"
      >
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Wyszukiwanie sugestii dokumentów kosztowych...
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="warning" sx={{ mb: 1 }} data-testid="expense-invoice-suggestions-error">
        {extractApiErrorMessage(error, 'Nie udało się pobrać sugestii dokumentów kosztowych.')}
      </Alert>
    );
  }

  const visibleSuggestions: BookingExpenseInvoiceSuggestionLookup[] = suggestions ?? [];

  if (visibleSuggestions.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1 }}
        data-testid="expense-invoice-suggestions-empty"
      >
        Brak sugestii dokumentów kosztowych dla tego bookingu.
      </Typography>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}
      data-testid="expense-invoice-suggestions-results"
    >
      <Typography variant="body2" fontWeight="bold">
        Sugerowane dokumenty kosztowe
      </Typography>
      {visibleSuggestions.map((suggestion) => (
        <Box
          key={suggestion.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1.5,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              {suggestion.documentReference} - {suggestion.displayIssuerName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(suggestion.issueDate).format('DD.MM.YYYY')} ·{' '}
              {suggestion.totalGross.toLocaleString('pl-PL', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              {suggestion.currency}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={confidenceLabels[suggestion.confidence]}
                color={confidenceColors[suggestion.confidence]}
              />
              {suggestion.reasons.map((reason) => (
                <Chip key={reason} size="small" variant="outlined" label={reason} />
              ))}
            </Stack>
          </Box>

          <Button size="small" variant="outlined" onClick={() => onSelectDocument(suggestion)}>
            Wybierz dokument
          </Button>
        </Box>
      ))}
    </Box>
  );
};

export default BookingExpenseInvoiceSuggestionsPanel;
